import React from 'react';
import {Popup, Image} from 'semantic-ui-react';
import PropTypes from 'prop-types';
import {sortBy as _sortBy} from 'lodash';

import {Authorization} from '@appComponents/Authorization';
import {IconEdit} from '@appComponents/IconCollection';
import {
    SECURITY_PRIVILEGES_READ,
    ORIGIN_STREAM_TYPE_RTMP,
    DISTRIBUTION_TYPE_LIVE,
    PRODUCT_THIRD_PARTY,
} from '@constants/variables';
import {BOOKING} from '@constants/resources';
import LinkWithAuthorization from '@appComponents/LinkWithAuthorization';
import {
    isWarningVisible,
    shouldRowBeGreen,
    shouldRowBeOrange,
    shouldRowBePurple,
    shouldRowBeRed,
    shouldRowBeYellow,
} from '@modules/liveControl/utils/helpers';
import ThirdPartyClientTunnelStatus from '@modules/thirdPartyClientTunnel/components/ThirdPartyClientTunnelStatus';

import {authorizationLiveControlUpdateObject} from './LiveControlTable';
import {getEntryPointNameAndWarningText} from './getEntryPointNameAndWarningText';

/**
 *
 * @param rowData
 * @param timezone
 * @returns {string}
 */
export const getRowClassname = (rowData, timezone) => {
    let classModifier = '';

    if (rowData.distributions_by_product) {
        if (shouldRowBeRed(rowData)) {
            classModifier = '--red';
        } else if (shouldRowBeOrange(rowData, timezone)) {
            classModifier = '--orange';
        } else if (shouldRowBeYellow(rowData)) {
            classModifier = '--yellow';
        } else if (shouldRowBeGreen(rowData)) {
            classModifier = '--green';
        } else if (shouldRowBePurple(rowData)) {
            classModifier = '--purple';
        }
    }

    return classModifier;
};

export const formatDistributionTypes = (distributionTypes) => {
    return _sortBy(distributionTypes, 'distribution_type_id').map(type => type.name).join(', ');
};

export const getFormattedDistribution = ({product_id, product_short_name, distribution_types, third_party_client_tunnels}) => {
    const formattedDistributionTypes = formatDistributionTypes(distribution_types);
    const productThirdParty = product_id === PRODUCT_THIRD_PARTY;
    const distributionTypeLive = distribution_types.some(type => type.distribution_type_id === DISTRIBUTION_TYPE_LIVE);

    return (
        <>
            {`${product_short_name} (${formattedDistributionTypes})`}
            &nbsp;
            {productThirdParty && distributionTypeLive && third_party_client_tunnels && (
                <ThirdPartyClientTunnelStatus thirdPartyClientTunnels={third_party_client_tunnels}/>
            )}
        </>
    );
};

export const renderDistributionRow = (distributionRow, index) => (
    <span key={`distributionRow-${index}`} style={{ whiteSpace: 'nowrap' }}>
        {distributionRow}
        <br/>
    </span>
);

export const renderDistributions = (distributions_by_product) => {
    return distributions_by_product ? distributions_by_product.map(getFormattedDistribution).map(renderDistributionRow) : '';
};

/**
 *
 * @param row
 */
export const renderCdns = (row) => {
    return row.distributions_by_product.map((dist) => {
        if (!dist.cdns) {
            return (
                <div key={`cdns-empty-${dist.product_short_name}`} className='cdnsRow' />
            );
        }

        return (
            <div key={`cdns-${dist.product_short_name}`} className='cdnsRow'>
                {renderCdnLogos(dist.cdns, dist.product_short_name)}
                <br/>
            </div>
        );
    });
};

const renderCdnLogos = (cdns, productShortName) => {
    return cdns.map((cdn) => {
        if (!cdn.logo_filename) {
            return null;
        }

        return (
            <Popup
                key={`tooltip-cdn-${cdn.id}-${productShortName}`}
                trigger={
                    <Image
                        key={`cdn-tt-${cdn.id}`}
                        className='cdnLogo'
                        size='tiny'
                        src={`/images/${cdn.logo_filename}`}
                    />
                }
                flowing
                hoverable
            >
                {cdn.name}
            </Popup>
        );
    });
};

/**
 *
 * @param row
 * @returns {string}
 */
export const renderBookings = (row) => {
    const distributions = row.distributions_by_product;

    return distributions ? distributions
        .map((distribution) => {
            return (
                <div key={row.event_content_id + '-distr-' + distribution.product_short_name}>
                    <LinkWithAuthorization
                        link={{
                            name: 'events.bookings.index',
                            params: {id: row.event_id},
                        }}
                        newWindow
                        authorization={{
                            [BOOKING]: SECURITY_PRIVILEGES_READ,
                        }}
                    >
                        {distribution.bookings_count}
                    </LinkWithAuthorization>
                    <br/>
                </div>
            );
        }) : '0';
};

export const getMatchStartedOrEnded = (value) => ((null === value) ? '-' : (value ? 'Yes' : 'No'));

export const isInFailover = (row) => {
    return !!row.is_srt_origin_entry_point_in_failover;
};

export const entryPointSortValue = (el) => (!isWarningVisible(el) || el.origin_stream_type_id === ORIGIN_STREAM_TYPE_RTMP)
    ? getEntryPointNameAndWarningText(el).name : null;

export const getSrtOriginEntryPointName = (row) => {
    if (isInFailover(row) && row.failover_srt_origin_entry_point_name) {
        return `${row.origin_stream_type_name} - ${row.failover_srt_origin_entry_point_name}`;
    }

    if (row.srt_origin_entry_point_name && row.srt_origin_entry_point_url) {
        return `${row.origin_stream_type_name} - ${row.srt_origin_entry_point_name}`;
    }

    return `${row.origin_stream_type_name}`;
};

export const EncodingModifyButton = (props) => {
    const onClick = () => {
        props.onClick(props.dataId);
    };

    return (
        <Authorization authorization={authorizationLiveControlUpdateObject}>
            <span className={'encodingModifyButton'} onClick={onClick}><IconEdit/></span>
        </Authorization>
    );
};
EncodingModifyButton.propTypes = {
    dataId: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
};
